/**
 * Multi-domain runtime resolution — the SAME Cloudflare Pages bundle is served
 * on several hosts, so resolve the BFF + SSO host from the SERVING domain at
 * runtime instead of trusting the baked VITE_* (mirrors the candidate SPA):
 *   - `*.eureca.me`                  → prd parallel stack (sso.eureca.me)
 *   - `*.eureca.dev` (NOT `*.stg.…`) → .dev stack         (sso.eureca.dev)
 *   - `*.stg.eureca.dev` / localhost → baked VITE_* (legacy staging keeps
 *                                      sso.stg.eureca.dev + recruiter-api.stg…)
 * Keycloak runs in dynamic-hostname mode, so each host mints matching issuers.
 * Without the `onEurecaDev` branch, prd `.dev` hosts (consultor/recrutador.
 * eureca.dev) fell back to the baked stg `VITE_KEYCLOAK_URL` and hit
 * `sso.stg.eureca.dev` — the bug this fixes.
 */
function onEurecaMe(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'eureca.me' || h.endsWith('.eureca.me');
}

/**
 * Slots de QA (spec 086): hosts de um nível `qa<N>-<app>.eureca.dev` no mesmo
 * sufixo do canary. O slot-spas.sh embute o BFF (`qa<N>-recruiter-api`) e o
 * Keycloak (`qa<N>-auth`) do próprio slot nos VITE_*, então o slot fica FORA do
 * `onEurecaDev` — senão o SPA do slot falaria com a stack canary.
 */
export function slotOfHostname(h: string): 'qa1' | 'qa2' | null {
  const m = /^(qa[12])-[a-z-]+\.eureca\.dev$/.exec(h);
  return m ? (m[1] as 'qa1' | 'qa2') : null;
}

const SLOT = typeof window === 'undefined' ? null : slotOfHostname(window.location.hostname);

/**
 * Efêmero por PR (spec 070): preview 1-nível `<app>-pr<N>.eureca.dev`, com o BFF
 * (`recruiter-api-pr<N>`) e o Keycloak (`auth-pr<N>`) do PR embutidos pelo
 * build-spas.sh. A distinção efêmero×canary é o LABEL `-pr<N>`, nunca
 * `.eureca.dev` cru (senão o SPA do PR falaria com a stack canary — caça BR-1,
 * spec 125). Vale enquanto o efêmero por PR existir, ao lado da regra dos slots.
 */
export function isEphemeralHostname(h: string): boolean {
  return /-pr\d+\.eureca\.dev$/.test(h);
}

const EPHEMERAL = typeof window !== 'undefined' && isEphemeralHostname(window.location.hostname);

function onEurecaDev(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  // Prd/canary on the .dev domain — but NOT the legacy `*.stg.eureca.dev`
  // staging hosts (e.g. recrutador.stg.eureca.dev), which keep their baked
  // VITE_* so they stay on sso.stg.eureca.dev — nor the QA slots, nor the
  // per-PR ephemeral previews.
  return (
    (h === 'eureca.dev' || h.endsWith('.eureca.dev')) &&
    !h.endsWith('.stg.eureca.dev') &&
    SLOT === null &&
    !EPHEMERAL
  );
}

const API_URL = onEurecaMe()
  ? 'https://recruiter-api.eureca.me'
  : onEurecaDev()
    ? 'https://recruiter-api.eureca.dev'
    : import.meta.env.VITE_API_URL || 'http://localhost:3003';

/**
 * EU-408 — base origin of the CANDIDATE SPA, used to build the private link a
 * consultor shares with the contracting company (the candidate opens it to
 * preview the program / job). Resolved from the serving domain so the SAME
 * Cloudflare bundle points at the matching candidate stack: prd `*.eureca.me`
 * → `app.eureca.me`, staging/canary `*.eureca.dev` → `app.eureca.dev`, QA slot
 * → the candidate of the SAME slot (`qa<N>-candidate.eureca.dev`; slot-spas.sh
 * does not bake this URL). Local dev uses the baked `VITE_CANDIDATE_APP_URL`
 * (default `http://localhost:5173`, the candidate SPA's vite port).
 */
const CANDIDATE_APP_URL = onEurecaMe()
  ? 'https://app.eureca.me'
  : onEurecaDev()
    ? 'https://app.eureca.dev'
    : SLOT
      ? `https://${SLOT}-candidate.eureca.dev`
      : import.meta.env.VITE_CANDIDATE_APP_URL || 'http://localhost:5173';

const KEYCLOAK_URL = onEurecaMe()
  ? 'https://sso.eureca.me'
  : onEurecaDev()
    ? 'https://sso.eureca.dev'
    : (import.meta.env.VITE_KEYCLOAK_URL as string);

// Slot de QA ou efêmero por PR: silent-check same-origin (servido pelo nginx do
// slot / do PR).
const SILENT_CHECK_SSO_REDIRECT_URI =
  onEurecaMe() || onEurecaDev() || SLOT !== null || EPHEMERAL
    ? `${window.location.origin}/silent-check-sso.html`
    : (import.meta.env.VITE_KEYCLOAK_SILENT_CHECK_SSO_REDIRECT_URI as string | undefined) ||
      undefined;

export const recruiterEnv = {
  appName: import.meta.env.VITE_APP_NAME || 'recruiter',
  appDisplayName: import.meta.env.VITE_APP_DISPLAY_NAME || 'EURECA Recruiter',
  apiUrl: API_URL,
  /** EU-408 — candidate SPA origin for building shareable private links. */
  candidateAppUrl: CANDIDATE_APP_URL,
  keycloak: {
    url: KEYCLOAK_URL,
    realm: import.meta.env.VITE_KEYCLOAK_REALM as string,
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID as string,
    onLoad:
      import.meta.env.VITE_KEYCLOAK_ON_LOAD === 'login-required'
        ? ('login-required' as const)
        : ('check-sso' as const),
    silentCheckSsoRedirectUri: SILENT_CHECK_SSO_REDIRECT_URI,
  },
} as const;
